// src/theme/roleThemes.ts

import { createTheme, ThemeOptions } from "@mui/material/styles";

// ============================================================================
// SHARED COMPONENT OVERRIDES - Applied to all role themes
// ============================================================================
const sharedComponents = {
  MuiPaper: {
    styleOverrides: {
      root: {
        backgroundImage: "none", // Remove default gradient
      },
      elevation1: {
        boxShadow:
          "0px 1px 3px rgba(0, 0, 0, 0.08), 0px 1px 2px rgba(0, 0, 0, 0.06)",
      },
      elevation2: {
        boxShadow:
          "0px 2px 4px rgba(0, 0, 0, 0.08), 0px 1px 3px rgba(0, 0, 0, 0.06)",
      },
      elevation3: {
        boxShadow:
          "0px 4px 6px rgba(0, 0, 0, 0.08), 0px 2px 4px rgba(0, 0, 0, 0.06)",
      },
    },
    defaultProps: {
      elevation: 1, // Default subtle shadow
    },
  },
  MuiCard: {
    styleOverrides: {
      root: {
        borderRadius: 12,
        border: "1px solid rgba(0, 0, 0, 0.08)", // Subtle border for definition
      },
    },
    defaultProps: {
      elevation: 1,
    },
  },
  MuiButton: {
    styleOverrides: {
      root: {
        borderRadius: 8,
        textTransform: "none" as const,
        fontWeight: 600,
      },
      contained: {
        boxShadow: "0px 1px 2px rgba(0, 0, 0, 0.08)",
        "&:hover": {
          boxShadow: "0px 2px 4px rgba(0, 0, 0, 0.12)",
        },
      },
    },
  },
  MuiChip: {
    styleOverrides: {
      root: {
        borderRadius: 6,
      },
    },
  },
  MuiLinearProgress: {
    styleOverrides: {
      root: {
        borderRadius: 4,
        height: 8,
        backgroundColor: "rgba(0, 0, 0, 0.08)", // Visible track
      },
    },
  },
};

// ============================================================================
// STUDENT THEME - Blue (Trust, Focus, Learning)
// ============================================================================
export const studentLightTheme: ThemeOptions = {
  palette: {
    mode: "light",
    primary: {
      main: "#1976D2",
      light: "#42A5F5",
      dark: "#0D47A1",
      contrastText: "#FFFFFF",
    },
    secondary: {
      main: "#FFC107",
      light: "#FFD54F",
      dark: "#FFA000",
      contrastText: "#000000",
    },
    background: {
      default: "#F0F4F8", // Light blue-gray - creates contrast with white cards
      paper: "#FFFFFF", // White cards
    },
    text: {
      primary: "#1A202C", // Near black - high readability
      secondary: "#4A5568", // Medium gray
    },
    divider: "rgba(0, 0, 0, 0.08)",
    action: {
      hover: "rgba(25, 118, 210, 0.04)",
      selected: "rgba(25, 118, 210, 0.08)",
    },
  },
  shape: {
    borderRadius: 8,
  },
  typography: {
    fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
    h4: {
      fontWeight: 700,
    },
    h5: {
      fontWeight: 600,
    },
    h6: {
      fontWeight: 600,
    },
    subtitle1: {
      fontWeight: 500,
    },
    button: {
      fontWeight: 600,
    },
  },
  components: {
    ...sharedComponents,
  },
};

// ============================================================================
// TEACHER THEME - Teal (Growth, Guidance, Nurturing)
// ============================================================================
export const teacherLightTheme: ThemeOptions = {
  palette: {
    mode: "light",
    primary: {
      main: "#00897B",
      light: "#4DB6AC",
      dark: "#00695C",
      contrastText: "#FFFFFF",
    },
    secondary: {
      main: "#FF7043",
      light: "#FF8A65",
      dark: "#E64A19",
      contrastText: "#FFFFFF",
    },
    background: {
      default: "#F0F5F4", // Light teal-gray
      paper: "#FFFFFF",
    },
    text: {
      primary: "#1A202C",
      secondary: "#4A5568",
    },
    divider: "rgba(0, 0, 0, 0.08)",
    action: {
      hover: "rgba(0, 137, 123, 0.04)",
      selected: "rgba(0, 137, 123, 0.08)",
    },
  },
  shape: {
    borderRadius: 8,
  },
  typography: {
    fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
    h4: { fontWeight: 700 },
    h5: { fontWeight: 600 },
    h6: { fontWeight: 600 },
    subtitle1: { fontWeight: 500 },
    button: { fontWeight: 600 },
  },
  components: {
    ...sharedComponents,
  },
};

// ============================================================================
// ADMIN THEME - Deep Purple (Authority, Wisdom, Control)
// ============================================================================
export const adminLightTheme: ThemeOptions = {
  palette: {
    mode: "light",
    primary: {
      main: "#5E35B1",
      light: "#7E57C2",
      dark: "#4527A0",
      contrastText: "#FFFFFF",
    },
    secondary: {
      main: "#26A69A",
      light: "#4DB6AC",
      dark: "#00897B",
      contrastText: "#FFFFFF",
    },
    background: {
      default: "#F3F0F8", // Light purple-gray
      paper: "#FFFFFF",
    },
    text: {
      primary: "#1A202C",
      secondary: "#4A5568",
    },
    divider: "rgba(0, 0, 0, 0.08)",
    action: {
      hover: "rgba(94, 53, 177, 0.04)",
      selected: "rgba(94, 53, 177, 0.08)",
    },
  },
  shape: {
    borderRadius: 8,
  },
  typography: {
    fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
    h4: { fontWeight: 700 },
    h5: { fontWeight: 600 },
    h6: { fontWeight: 600 },
    subtitle1: { fontWeight: 500 },
    button: { fontWeight: 600 },
  },
  components: {
    ...sharedComponents,
  },
};

// ============================================================================
// DARK THEMES (with proper contrast)
// ============================================================================
const sharedDarkComponents = {
  MuiPaper: {
    styleOverrides: {
      root: {
        backgroundImage: "none",
        border: "1px solid rgba(255, 255, 255, 0.08)",
      },
    },
    defaultProps: {
      elevation: 0,
    },
  },
  MuiCard: {
    styleOverrides: {
      root: {
        borderRadius: 12,
        border: "1px solid rgba(255, 255, 255, 0.1)",
        backgroundColor: "#1E1E1E",
      },
    },
  },
  MuiLinearProgress: {
    styleOverrides: {
      root: {
        borderRadius: 4,
        height: 8,
        backgroundColor: "rgba(255, 255, 255, 0.12)",
      },
    },
  },
};

export const studentDarkTheme: ThemeOptions = {
  palette: {
    mode: "dark",
    primary: {
      main: "#64B5F6",
      light: "#90CAF9",
      dark: "#1976D2",
      contrastText: "#000000",
    },
    secondary: {
      main: "#FFD54F",
      light: "#FFE082",
      dark: "#FFC107",
      contrastText: "#000000",
    },
    background: {
      default: "#0A0A0A",
      paper: "#1A1A1A",
    },
    text: {
      primary: "#F7FAFC",
      secondary: "#A0AEC0",
    },
    divider: "rgba(255, 255, 255, 0.08)",
  },
  components: {
    ...sharedDarkComponents,
  },
};

export const teacherDarkTheme: ThemeOptions = {
  palette: {
    mode: "dark",
    primary: {
      main: "#4DB6AC",
      light: "#80CBC4",
      dark: "#00897B",
      contrastText: "#000000",
    },
    secondary: {
      main: "#FF8A65",
      light: "#FFAB91",
      dark: "#FF7043",
      contrastText: "#000000",
    },
    background: {
      default: "#0A0A0A",
      paper: "#1A1A1A",
    },
    text: {
      primary: "#F7FAFC",
      secondary: "#A0AEC0",
    },
    divider: "rgba(255, 255, 255, 0.08)",
  },
  components: {
    ...sharedDarkComponents,
  },
};

export const adminDarkTheme: ThemeOptions = {
  palette: {
    mode: "dark",
    primary: {
      main: "#B39DDB",
      light: "#D1C4E9",
      dark: "#7E57C2",
      contrastText: "#000000",
    },
    secondary: {
      main: "#4DB6AC",
      light: "#80CBC4",
      dark: "#26A69A",
      contrastText: "#000000",
    },
    background: {
      default: "#0A0A0A",
      paper: "#1A1A1A",
    },
    text: {
      primary: "#F7FAFC",
      secondary: "#A0AEC0",
    },
    divider: "rgba(255, 255, 255, 0.08)",
  },
  components: {
    ...sharedDarkComponents,
  },
};

// ============================================================================
// THEME FACTORY
// ============================================================================
export const createRoleTheme = (
  role: "student" | "teacher" | "admin",
  mode: "light" | "dark"
) => {
  const themeMap = {
    student: { light: studentLightTheme, dark: studentDarkTheme },
    teacher: { light: teacherLightTheme, dark: teacherDarkTheme },
    admin: { light: adminLightTheme, dark: adminDarkTheme },
  };

  return createTheme(themeMap[role][mode]);
};

export const themes = {
  student: {
    light: createTheme(studentLightTheme),
    dark: createTheme(studentDarkTheme),
  },
  teacher: {
    light: createTheme(teacherLightTheme),
    dark: createTheme(teacherDarkTheme),
  },
  admin: {
    light: createTheme(adminLightTheme),
    dark: createTheme(adminDarkTheme),
  },
};
